<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue'
  import { useAppConfig } from 'nuxt/app'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import useOutbrainAds, {
    type OutbrainElementData,
  } from '../../composables/ad-ops/useOutbrainAds'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import { createExtrnId } from '../../utils/string/uuid'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'

  interface OutbrainWidgetAttributes {
    'data-ob-portalUrl': string
    'data-ob-language': string
    'data-widget-id': string
    'data-external-id': string
    'data-ob-installation-key': string
    'data-external-secondary-id': string
    'data-render-registered': boolean
  }

  /**
   * Props for the Outbrain component
   * @property placement - The placement identifier for the ad
   * @property widgetId - The Outbrain widget ID
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface OutbrainProps {
    placement: string
    widgetId: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<OutbrainProps>(), {
    tilePosition: -1,
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  if (!props.placement || !props.widgetId) {
    throw new Error(
      `Outbrain component requires placement and widgetId props- ${props.placement}`
    )
  }

  const { outbrain } = useAppConfig()
  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()

  const { track } = useAnalytics()

  const { callRenderResearchWidgets, triggerOutbrainRenderedEvent } =
    useOutbrainAds()

  const placementClassName = ref<string>(props.placement)
  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')
  const adBrand = ref<string>('')

  const widgetAttributes = computed<OutbrainWidgetAttributes>(() => {
    const widgetAttributes = {
      'data-ob-portalUrl': '',
      'data-ob-language': 'en',
      'data-widget-id': props.widgetId,
      'data-external-id': '',
      'data-ob-installation-key': outbrain?.installationKey || '',
      'data-external-secondary-id': createExtrnId(),
      'data-render-registered': false,
    }

    if (import.meta.client) {
      widgetAttributes[`data-ob-portalUrl`] = getTruncatedUrl()
      widgetAttributes[`data-external-id`] = window.location.host
    }

    return widgetAttributes
  })
  function setAdBrand(element: Element) {
    adBrand.value = element?.querySelector('.ob-rec-source')?.textContent || ''
  }

  function getEventData(): NtpAdEvent {
    const eventData: NtpAdEvent = {
      ntp_tile_position: props.tilePosition,
      ntp_ad_provider: 'outbrain',
      ntp_creative_id: props.widgetId,
      ntp_placement_name: props.placement,
      ntp_ad_brand: adBrand.value,
    }

    return eventData
  }

  function handleAdClick() {
    adClicked(getEventData(), props.widgetId)
  }

  function attachClickEventToAnchorTags(element: Element) {
    const anchorTag = element.querySelector('a')

    if (anchorTag && !anchorTag.getAttribute('data-click-registered')) {
      anchorTag.setAttribute('data-click-registered', 'true')
      anchorTag.addEventListener('click', handleAdClick)
    }
  }

  function detachClickEventFromAnchorTags(element: Element) {
    // Find all anchor tags with our registered click handlers and remove the listeners
    const registeredAnchors = element.querySelectorAll(
      'a[data-click-registered="true"]'
    )
    registeredAnchors.forEach((anchor) => {
      anchor.removeEventListener('click', handleAdClick)
      anchor.removeAttribute('data-click-registered')
    })
  }

  function handleOutbrainRenderedEvent(data: OutbrainElementData) {
    // Outbrain Render Event Handler
    //
    // The 'rendered' event fires multiple times during the ad rendering process:
    // 1. Initial fires: Returns wrapper element only (no actual ad content)
    // 2. DOM attachment: Element may not be in DOM tree yet (no parentElement)
    // 3. Final render: Ad is fully rendered and attached to DOM
    //
    // Implementation Strategy:
    // - Use parentElement presence as render completion indicator
    // - Verify correct placement via expected class name
    // - Track render status to prevent duplicate processing
    // - Process only first successful render
    // - Mark as rendered after processing

    const element: Element | null = data.element
    const parentElement: HTMLElement | null =
      element?.parentElement?.parentElement?.parentElement || null
    const isRendered =
      parentElement?.getAttribute('data-render-registered') === 'true'
    const hasExpectedClass = parentElement?.classList.contains(props.placement)

    if (parentElement && !isRendered && hasExpectedClass) {
      // Set ad brand for analytics
      setAdBrand(element)

      attachClickEventToAnchorTags(data.element)

      track('NtpAd_Rendered', getEventData())

      parentElement.setAttribute('data-render-registered', 'true')
    }
  }

  const renderOutbrainElement = ref(false)

  function renderOutbrainAd() {
    renderOutbrainElement.value = true

    nextTick(() => {
      if (
        !widgetWrapperEl.value ||
        widgetWrapperEl.value?.getAttribute('data-render-registered')
      )
        return

      const index = getIndexOfPlacement(props.placement, widgetWrapperEl.value)

      placementClassName.value =
        index > 0
          ? `${props.placement} ${props.placement}-${index}`
          : props.placement

      callRenderResearchWidgets()

      triggerOutbrainRenderedEvent(props.widgetId, handleOutbrainRenderedEvent)
    })
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      widgetWrapperEl.value,
      props.lazyRender,
      props.sticky,
      renderOutbrainAd
    )
  })

  onUnmounted(() => {
    if (widgetWrapperEl.value) {
      detachClickEventFromAnchorTags(widgetWrapperEl.value)
    }
  })
</script>

<template>
  <div ref="widgetWrapperEl" :class="placementClassName">
    <div
      v-if="renderOutbrainElement"
      class="OUTBRAIN"
      :class="placementClassName"
      v-bind="widgetAttributes"
    />
  </div>
</template>
